// Replaces TrailMap/AppleMapProvider.swift (TrailBivvyKit) on the web: Apple Maps does not exist in a browser, so the
// last fallback is OpenStreetMap's standard tile layer (https://tile.openstreetmap.org), online only.
//
// OSMF tile usage policy (https://operations.osmfoundation.org/policies/tiles/, read 2026-10-20), applied here:
// - attribution "© OpenStreetMap contributors" clearly visible on the map, linked to the copyright page;
// - "download for offline" and preloading regions or zoom levels are forbidden: `allowsOfflineStorage` is false, the
//   layer is never part of an offline package, and the service worker never stores its tiles (browser HTTP cache only,
//   which honours the server's caching headers);
// - web use must keep a valid Referer: the map never sets a restrictive referrer policy on these tiles;
// - commercial users get best-effort access that "may be withdrawn at any point": the app keeps working without it
//   (official national maps first, route and data offline).
//
// Like the Apple provider, the style has no raster base layer of its own: `usesFallbackBaseMap` tells the map to draw
// `OpenStreetMapProvider.standard` underneath. Unlike Apple (MapKit draws its own legal label), the style carries the
// OSM attribution. Vector overlays (route, protected areas, POIs) still work offline from local data, and route-only
// offline regions can still be downloaded.

import type { EpochMs, GeoBounds, OfflinePackageLevel, OfflineRegion, ProtectedArea, TrailRoute } from '../core/models';
import { makeTileLayerDescriptor, type TileLayerDescriptor } from '../offline/TileLayerDescriptor';
import type { MapLayerPreferences } from './MapLayer';
import { RouteRegionSplitter } from './RouteRegionSplitter';
import { corsEnabledFor } from './tileHosts';
import {
  MapAttributions,
  noLocalProtectedAreas,
  type LocalProtectedAreas,
  type MapAttribution,
  type MapStyle,
  type TrailMapProvider,
} from './TrailMapProvider';

const osmTemplate = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export class OpenStreetMapProvider implements TrailMapProvider {
  readonly id = 'osm';
  readonly displayName = 'OpenStreetMap';
  /** The whole world. */
  readonly coverage: GeoBounds = { minLatitude: -90, minLongitude: -180, maxLatitude: 90, maxLongitude: 180 };

  /**
   * OpenStreetMap standard ("Carto") tiles: PNG, z0–19, ~21 KB at z12 (measured 2026-10-20). Never stored for offline
   * use (`allowsOfflineStorage` false), worldwide.
   */
  static readonly standard: TileLayerDescriptor = makeTileLayerDescriptor({
    id: 'org.openstreetmap.standard',
    urlTemplate: osmTemplate,
    fileExtension: 'png',
    minZoom: 0,
    maxZoom: 19,
    averageTileBytes: 20_000,
    attribution: MapAttributions.openStreetMap.text,
    isOpaque: true,
    allowsOfflineStorage: false,
    corsEnabled: corsEnabledFor(osmTemplate),
  });

  private readonly localProtectedAreas: LocalProtectedAreas;

  /** @param localProtectedAreas Areas from the bundled/offline datasets for the given bounds. */
  constructor(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas) {
    this.localProtectedAreas = localProtectedAreas;
  }

  /** The fallback base map only, no raster layers; the OSM credit. */
  mapStyle(_bounds: GeoBounds, _preferences: MapLayerPreferences): MapStyle {
    return { rasterOverlays: [], usesFallbackBaseMap: true, attributions: [MapAttributions.openStreetMap] };
  }

  attributionsFor(_layers: readonly TileLayerDescriptor[]): MapAttribution[] {
    return [MapAttributions.openStreetMap];
  }

  /** Always empty (offline use of tile.openstreetmap.org is forbidden by the OSMF tile usage policy). */
  offlineTileLayers(_level: OfflinePackageLevel): TileLayerDescriptor[] {
    return [];
  }

  /** Route-data-only regions of ≤ 40 km (stage boundaries when possible). */
  downloadableRegions(route: TrailRoute, level: OfflinePackageLevel, now: EpochMs): OfflineRegion[] {
    return RouteRegionSplitter.regions(route, level, [], now);
  }

  /** Local areas only. */
  protectedAreas(bounds: GeoBounds): Promise<ProtectedArea[]> {
    return this.localProtectedAreas(bounds);
  }
}
